import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Send, X, Sparkles, Headset, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { SUPPORT_EMAIL } from "@/lib/company";
import { useAuth } from "@/lib/auth";
import { getPromoConfig, chatbotPromoQuestion } from "@/lib/promo";

const BASE_SUGGESTIONS = [
    "Come pubblico un annuncio?",
    "Quali sono i piani?",
    "Come scrivo un titolo efficace?",
];

export default function Chatbot() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Ciao! Sono l'assistente di vendidirect.it. Posso aiutarti con annunci, abbonamenti e suggerimenti per vendere. Come posso esserti utile?" },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [suggestions, setSuggestions] = useState(BASE_SUGGESTIONS);
    const scrollRef = useRef(null);
    const location = useLocation();
    const { user } = useAuth();
    // In VD Manager: posizione in basso a destra + icona rotonda con omino
    const isVdManager = location.pathname.startsWith("/analytics-pro");
    const posBtn = isVdManager ? "bottom-6 right-6" : "bottom-6 left-6";
    const posPanel = isVdManager ? "bottom-28 right-6" : "bottom-28 left-6";

    // Modalità supporto umano ("Parla con noi") — attiva solo per agenzie loggate
    const [mode, setMode] = useState("ai"); // "ai" | "human"
    const [convId, setConvId] = useState(null);
    const [supportMsgs, setSupportMsgs] = useState([]);
    const [supportInput, setSupportInput] = useState("");
    const [supportSending, setSupportSending] = useState(false);
    const canRequestHuman = !!user && user.role === "agenzia";

    // Cambia il messaggio di benvenuto in base al contesto
    useEffect(() => {
        if (isVdManager && user) {
            setMessages([
                { role: "assistant", content: `Ciao ${user.agency_name || user.name || ""}! Sono l'assistente del VD Manager. Posso guidarti nelle sezioni del CRM, spiegarti funzionalità e aiutarti nell'uso quotidiano. Se hai bisogno di parlare con lo staff, clicca "Parla con noi".` },
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVdManager, user?.user_id]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    useEffect(() => {
        getPromoConfig().then((cfg) => {
            const q = chatbotPromoQuestion(cfg);
            setSuggestions(q ? [...BASE_SUGGESTIONS, q] : BASE_SUGGESTIONS);
        });
    }, []);

    const send = async (text) => {
        const content = (text || input).trim();
        if (!content || sending) return;
        const next = [...messages, { role: "user", content }];
        setMessages(next);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/chatbot/chat", { messages: next, session_id: sessionId, context: isVdManager ? "vd_manager" : undefined });
            setSessionId(data.session_id);
            setMessages([...next, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages([...next, { role: "assistant", content: "Si è verificato un errore, riprova." }]);
        } finally {
            setSending(false);
        }
    };

    // ------------ Modalità "Parla con noi" (agenzia ↔ staff) ------------
    const startHumanChat = async () => {
        try {
            const { data } = await api.post("/support/conversation/open");
            setConvId(data.id);
            const r = await api.get(`/support/conversation/${data.id}/messages`);
            setSupportMsgs(r.data.messages || []);
            setMode("human");
        } catch (e) {
            // fallback
            setMessages((prev) => [...prev, { role: "assistant", content: "Impossibile aprire il canale di supporto in questo momento. Riprova più tardi." }]);
        }
    };

    const sendSupport = async (e) => {
        e && e.preventDefault && e.preventDefault();
        const text = supportInput.trim();
        if (!text || supportSending || !convId) return;
        setSupportSending(true);
        try {
            const { data } = await api.post(`/support/conversation/${convId}/message`, { text });
            setSupportMsgs((prev) => [...prev, data]);
            setSupportInput("");
        } catch { /* keep input */ }
        finally { setSupportSending(false); }
    };

    // Polling per risposte admin quando in modalità supporto
    useEffect(() => {
        if (mode !== "human" || !convId || !open) return;
        const t = setInterval(async () => {
            const last = supportMsgs.length ? supportMsgs[supportMsgs.length - 1].created_at : null;
            try {
                const { data } = await api.get(`/support/conversation/${convId}/messages${last ? `?since=${encodeURIComponent(last)}` : ""}`);
                const fresh = data.messages || [];
                if (fresh.length) setSupportMsgs((prev) => [...prev, ...fresh]);
            } catch { /* ignore */ }
        }, 4000);
        return () => clearInterval(t);
    }, [mode, convId, open, supportMsgs]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [supportMsgs]);

    return (
        <>
            <button
                data-testid="chatbot-toggle"
                onClick={() => setOpen(!open)}
                className={`fixed ${posBtn} z-[60] transition-transform duration-300 hover:-translate-y-1 group`}
                aria-label="Apri assistente"
                style={{ filter: "drop-shadow(0 8px 22px rgba(37,99,235,0.45))" }}
            >
                {open ? (
                    <div className={`w-14 h-14 flex items-center justify-center ${isVdManager ? "rounded-full bg-white text-blue-600 border border-slate-200 shadow-[0_8px_20px_rgba(15,23,42,0.25)]" : "bg-gold text-black"}`}>
                        <X size={22} />
                    </div>
                ) : isVdManager ? (
                    <div data-testid="chatbot-vd-round" className="relative w-14 h-14 flex items-center justify-center">
                        <img src="/assets/chatbot-icon.png" alt="Assistente VD Manager" className="w-14 h-14 object-contain" draggable="false" />
                        <span className="absolute top-0 right-0 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-white" />
                    </div>
                ) : (
                    <div className="relative w-[68px] h-[76px] flex items-center justify-center">
                        <img src="/assets/chatbot-icon.png" alt="Assistente vendidirect" className="w-[68px] h-[76px] object-contain" draggable="false" />
                        <span className="absolute top-0 right-1.5 w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse border border-white" />
                    </div>
                )}
            </button>

            {open && (
                <div data-testid="chatbot-panel" className={`fixed ${posPanel} z-[60] w-[92vw] max-w-md bg-ink-950 border border-gold/40 shadow-2xl flex flex-col overscroll-contain`} style={{ height: "min(72dvh, 620px)", maxHeight: "calc(100dvh - 8rem)" }}>
                    <div className="p-5 border-b border-white/10 flex items-center gap-3">
                        <div className="w-10 h-10 bg-gold flex items-center justify-center">
                            {mode === "human" ? <Headset size={16} className="text-black" /> : <Sparkles size={16} className="text-black" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-serif text-lg text-ink-50">
                                {mode === "human" ? "Supporto VendiDirect" : (isVdManager ? "Assistente VD Manager" : "Assistente vendidirect")}
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-gold">{mode === "human" ? "chat con lo staff" : "online"}</div>
                        </div>
                        {mode === "human" && (
                            <button data-testid="chatbot-back-ai" onClick={() => setMode("ai")} className="text-ink-400 hover:text-gold p-1" title="Torna all'assistente"><ArrowLeft size={16} /></button>
                        )}
                    </div>

                    {mode === "human" ? (
                        <>
                            <div ref={scrollRef} data-testid="support-messages" className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-3">
                                <div className="text-[11px] text-ink-400 border border-white/5 bg-black/40 p-3 leading-relaxed">
                                    Sei in contatto diretto con lo staff VendiDirect. I tuoi dati (nome agenzia, email, piano) sono condivisi automaticamente per assisterti.
                                    <div className="mt-2">Email: <a data-testid="chatbot-support-email" href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a></div>
                                </div>
                                {supportMsgs.length === 0 && (
                                    <div className="text-center text-ink-500 text-xs py-6">Scrivi il tuo primo messaggio, ti risponderemo appena possibile.</div>
                                )}
                                {supportMsgs.map((m) => {
                                    const agency = m.sender === "agency";
                                    return (
                                        <div key={m.id} className={`flex ${agency ? "justify-end" : "justify-start"}`}>
                                            <div
                                                data-testid={agency ? "support-bubble-out" : "support-bubble-in"}
                                                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl break-words whitespace-pre-wrap shadow-[0_4px_10px_rgba(0,0,0,0.28),0_1px_2px_rgba(0,0,0,0.18)] ${agency ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900 border border-gray-200"}`}
                                            >
                                                {m.sender === "admin" && <div className="text-[10px] text-blue-700 tracking-wider uppercase mb-1 font-semibold">Staff</div>}
                                                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={sendSupport} className="p-3 border-t border-white/10 flex gap-2 items-center" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
                                <input
                                    data-testid="support-input"
                                    value={supportInput}
                                    onChange={(e) => setSupportInput(e.target.value)}
                                    placeholder="Scrivi allo staff..."
                                    className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-base sm:text-sm text-ink-50 focus:border-gold outline-none min-h-[44px]"
                                />
                                <button data-testid="support-send" disabled={supportSending} className="bg-gold text-black px-4 hover:bg-gold-soft disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"><Send size={16} /></button>
                            </form>
                        </>
                    ) : (
                        <>
                    <div ref={scrollRef} data-testid="chatbot-messages" className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 space-y-4">
                        {messages.map((m, i) => {
                            const mine = m.role === "user";
                            return (
                                <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                    <div
                                        data-testid={mine ? "chatbot-bubble-out" : "chatbot-bubble-in"}
                                        className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed rounded-2xl break-words whitespace-pre-wrap shadow-[0_4px_10px_rgba(0,0,0,0.28),0_1px_2px_rgba(0,0,0,0.18)] ${mine ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900 border border-gray-200"}`}
                                    >
                                        {m.content}
                                    </div>
                                </div>
                            );
                        })}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="px-4 py-3 text-sm bg-gray-100 text-gray-500 border border-gray-200 rounded-2xl shadow-[0_4px_10px_rgba(0,0,0,0.28),0_1px_2px_rgba(0,0,0,0.18)]">
                                    <span className="inline-block w-2 h-2 rounded-full animate-bounce bg-blue-600" />
                                    <span className="inline-block w-2 h-2 rounded-full animate-bounce ml-1 bg-blue-600" style={{ animationDelay: "0.15s" }} />
                                    <span className="inline-block w-2 h-2 rounded-full animate-bounce ml-1 bg-blue-600" style={{ animationDelay: "0.3s" }} />
                                </div>
                            </div>
                        )}
                    </div>

                    {messages.length <= 1 && (
                        <div className="px-4 sm:px-5 pb-3 flex flex-wrap gap-2">
                            {suggestions.map((s) => (
                                <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 border border-white/10 text-ink-100 hover:border-gold hover:text-gold transition-colors min-h-[36px]">
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    {canRequestHuman && (
                        <div className="px-4 sm:px-5 pb-3">
                            <button
                                data-testid="chatbot-talk-to-us"
                                onClick={startHumanChat}
                                className="w-full flex items-center justify-center gap-2 text-xs px-3 py-2.5 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors min-h-[44px]"
                            >
                                <Headset size={12} /> Parla con noi (staff VendiDirect)
                            </button>
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex gap-2 items-center" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
                        <input
                            data-testid="chatbot-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Scrivi un messaggio..."
                            className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-base sm:text-sm text-ink-50 focus:border-gold outline-none min-h-[44px]"
                        />
                        <button data-testid="chatbot-send" disabled={sending} className="bg-gold text-black px-4 hover:bg-gold-soft disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"><Send size={16} /></button>
                    </form>
                        </>
                    )}
                </div>
            )}
        </>
    );
}
